import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/inquiries`;

export function InquiryForm({ category, categoryLabel }) {
  const [f, setF] = useState({ name: "", email: "", check_in: "", check_out: "", num_guests: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) { toast.error("Please share your name and email."); return; }
    setBusy(true);
    try {
      await axios.post(API, {
        category, name: f.name.trim(), email: f.email.trim(),
        check_in: f.check_in || null, check_out: f.check_out || null,
        num_guests: f.num_guests ? Number(f.num_guests) : null, message: f.message.trim() || null,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid={`inquiry-success-${category}`} className="rounded-2xl border border-[#E7E5E4] bg-[#F3F6F2] p-8 max-w-xl">
        <CheckCircle2 className="w-8 h-8 text-[#8A9A86]" strokeWidth={1.5} />
        <p className="mt-4 text-lg text-[#1C1917] font-normal tracking-tight">Thank you — we've received your inquiry.</p>
        <p className="mt-2 text-sm text-[#44403C] font-light leading-relaxed">
          Our team will reach out as soon as {categoryLabel.toLowerCase()} become available for your dates. No booking or
          payment is required yet.
        </p>
      </div>
    );
  }

  const input = "w-full rounded-xl border border-[#E7E5E4] px-3.5 py-2.5 text-sm text-[#1C1917] focus:outline-none focus:ring-1 focus:ring-[#8A9A86] bg-white";
  const label = "block text-xs tracking-[0.12em] uppercase text-[#8A9A86] font-medium mb-1.5";

  return (
    <form onSubmit={submit} data-testid={`inquiry-form-${category}`} className="rounded-2xl border border-[#E7E5E4] bg-white p-6 md:p-8 max-w-xl">
      <p className="text-xs tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Register your interest</p>
      <h3 className="mt-2 text-xl font-light tracking-tight text-[#1C1917]">Be first to know when homes go live</h3>
      <p className="mt-1.5 text-sm text-[#78716C] font-light">Tell us a little about your stay and we'll be in touch when a home fits your dates.</p>

      <div className="mt-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={label}>Name</label><input data-testid="inquiry-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className={input} /></div>
          <div><label className={label}>Email</label><input data-testid="inquiry-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className={input} /></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={label}>Preferred check-in</label><input data-testid="inquiry-checkin" type="date" value={f.check_in} onChange={(e) => set("check_in", e.target.value)} className={input} /></div>
          <div><label className={label}>Preferred check-out</label><input data-testid="inquiry-checkout" type="date" value={f.check_out} onChange={(e) => set("check_out", e.target.value)} className={input} /></div>
        </div>
        <div><label className={label}>Number of guests</label><input data-testid="inquiry-guests" type="number" min={1} max={50} value={f.num_guests} onChange={(e) => set("num_guests", e.target.value)} placeholder="e.g. 4" className={input + " sm:max-w-[160px]"} /></div>
        <div><label className={label}>Message (optional)</label><textarea data-testid="inquiry-message" rows={3} value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="Anything we should know about your stay?" className={input + " resize-none"} /></div>
      </div>

      <button type="submit" disabled={busy} data-testid={`inquiry-submit-${category}`}
        className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#8A9A86] text-white px-8 py-3.5 hover:bg-[#77876F] transition-colors duration-300 font-medium text-sm disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.75} />}
        {busy ? "Sending…" : "Send inquiry"}
      </button>
    </form>
  );
}
